import { create } from 'zustand';
import { projectService } from '@services/projectService';
import type {
  Project,
  ProjectCreateData,
  Sample,
  TestResult,
  ProjectStatistics,
} from '../types/api';

interface ProjectStore {
  projects: Project[];
  currentProject: Project | null;
  samples: Sample[];
  testResults: TestResult[];
  statistics: ProjectStatistics | null;

  isLoading: boolean;
  error: string | null;

  // Actions
  fetchProjects: () => Promise<void>;
  fetchProject: (id: string) => Promise<void>;
  createProject: (data: ProjectCreateData) => Promise<Project>;
  updateProject: (id: string, data: Partial<ProjectCreateData>) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;
  setCurrentProject: (project: Project | null) => void;

  // Sample actions
  fetchSamples: (projectId: string) => Promise<void>;
  uploadSample: (projectId: string, data: { object_name: string; image: File }) => Promise<Sample>;
  deleteSample: (projectId: string, sampleId: number) => Promise<void>;

  // Test actions
  fetchTestResults: (projectId: string) => Promise<void>;
  testModel: (projectId: string, image: File) => Promise<TestResult>;

  // Statistics
  fetchStatistics: (projectId: string) => Promise<void>;

  clearError: () => void;
}

export const useProjectStore = create<ProjectStore>((set) => ({
  projects: [],
  currentProject: null,
  samples: [],
  testResults: [],
  statistics: null,
  isLoading: false,
  error: null,

  fetchProjects: async () => {
    set({ isLoading: true, error: null });
    try {
      const projects = await projectService.getProjects();
      set({ projects, isLoading: false });
    } catch (error: any) {
      set({
        error: error.response?.data?.message || error.message || '获取项目列表失败',
        isLoading: false,
      });
    }
  },

  fetchProject: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const project = await projectService.getProject(id);
      set({ currentProject: project, isLoading: false });
    } catch (error: any) {
      set({
        error: error.response?.data?.message || error.message || '获取项目详情失败',
        isLoading: false,
      });
    }
  },

  createProject: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const project = await projectService.createProject(data);
      set((state) => ({
        projects: [...state.projects, project],
        currentProject: project,
        isLoading: false,
      }));
      return project;
    } catch (error: any) {
      set({
        error: error.response?.data?.message || error.message || '创建项目失败',
        isLoading: false,
      });
      throw error;
    }
  },

  updateProject: async (id, data) => {
    set({ isLoading: true, error: null });
    try {
      const updatedProject = await projectService.updateProject(id, data);
      set((state) => ({
        projects: state.projects.map((p) =>
          p.id === id ? { ...p, ...updatedProject } : p
        ),
        currentProject: state.currentProject?.id === id
          ? { ...state.currentProject, ...updatedProject }
          : state.currentProject,
        isLoading: false,
      }));
    } catch (error: any) {
      set({
        error: error.response?.data?.message || error.message || '更新项目失败',
        isLoading: false,
      });
      throw error;
    }
  },

  deleteProject: async (id) => {
    set({ isLoading: true, error: null });
    try {
      // 确保 id 是字符串格式的 UUID
      const projectId = String(id);
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(projectId)) {
        console.error('Invalid project ID (not UUID):', projectId);
        set({ error: `项目ID格式无效: ${projectId}，请刷新页面后重试`, isLoading: false });
        return;
      }
      await projectService.deleteProject(projectId);
      set((state) => ({
        projects: state.projects.filter((p) => String(p.id) !== projectId),
        currentProject: state.currentProject?.id === projectId ? null : state.currentProject,
        isLoading: false,
      }));
    } catch (error: any) {
      set({
        error: error.response?.data?.message || error.message || '删除项目失败',
        isLoading: false,
      });
      throw error;
    }
  },

  setCurrentProject: (project) => {
    set({ currentProject: project });
  },

  fetchSamples: async (projectId) => {
    set({ isLoading: true, error: null });
    try {
      const samples = await projectService.getSamples(projectId);
      set({ samples, isLoading: false });
    } catch (error: any) {
      set({
        error: error.response?.data?.message || error.message || '获取样本数据失败',
        isLoading: false,
      });
    }
  },

  uploadSample: async (projectId, data) => {
    set({ isLoading: true, error: null });
    try {
      const sample = await projectService.uploadSample(projectId, data);
      set((state) => ({
        samples: [...state.samples, sample],
        isLoading: false,
      }));
      return sample;
    } catch (error: any) {
      set({
        error: error.response?.data?.message || error.message || '上传样本失败',
        isLoading: false,
      });
      throw error;
    }
  },

  deleteSample: async (projectId, sampleId) => {
    set({ isLoading: true, error: null });
    try {
      await projectService.deleteSample(projectId, sampleId);
      set((state) => ({
        samples: state.samples.filter((s) => s.id !== sampleId),
        isLoading: false,
      }));
    } catch (error: any) {
      set({
        error: error.response?.data?.message || error.message || '删除样本失败',
        isLoading: false,
      });
      throw error;
    }
  },

  fetchTestResults: async (projectId) => {
    set({ isLoading: true, error: null });
    try {
      const testResults = await projectService.getTestResults(projectId);
      set({ testResults, isLoading: false });
    } catch (error: any) {
      set({
        error: error.response?.data?.message || error.message || '获取测试结果失败',
        isLoading: false,
      });
    }
  },

  testModel: async (projectId, image) => {
    set({ isLoading: true, error: null });
    try {
      const result = await projectService.testModel(projectId, { image });
      set((state) => ({
        testResults: [...state.testResults, result],
        isLoading: false,
      }));
      return result;
    } catch (error: any) {
      set({
        error: error.response?.data?.message || error.message || '测试模型失败',
        isLoading: false,
      });
      throw error;
    }
  },

  fetchStatistics: async (projectId) => {
    set({ isLoading: true, error: null });
    try {
      const statistics = await projectService.getStatistics(projectId);
      set({ statistics, isLoading: false });
    } catch (error: any) {
      set({
        error: error.response?.data?.message || error.message || '获取统计数据失败',
        isLoading: false,
      });
    }
  },

  clearError: () => set({ error: null }),
}));